"use client";

import type { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { type TenantContextValue, useTenant } from "@tillix/tenants/react";
import { baseDialogKit } from "./dialog";
import { conEdisonDialogKit } from "./dialog.con-edison";
import type { OptionalTenantProp } from "../../../types";

/**
 * Div props plus Base UI's `render`, so a description holding several
 * paragraphs can swap its default `<p>` for a block element (`<div />`).
 */
export type DialogDescriptionProps = React.ComponentProps<"div"> &
  Pick<DialogPrimitive.Description.Props, "render">;

export interface DialogParts {
  Root: React.FC<DialogPrimitive.Root.Props>;
  Trigger: React.FC<DialogPrimitive.Trigger.Props>;
  Portal: React.FC<DialogPrimitive.Portal.Props>;
  Close: React.FC<DialogPrimitive.Close.Props>;
  Backdrop: React.FC<DialogPrimitive.Backdrop.Props>;
  Popup: React.FC<DialogPrimitive.Popup.Props>;
  Header: React.FC<React.ComponentProps<"div">>;
  Title: React.FC<React.ComponentProps<"div">>;
  Description: React.FC<DialogDescriptionProps>;
}

function useDialogKit(tenantProp?: TenantContextValue) {
  const contextTenant = useTenant();
  const tenantRefId = tenantProp?.id ?? contextTenant?.id;

  switch (tenantRefId) {
    case "con-edison":
      return conEdisonDialogKit;
    default:
      return baseDialogKit;
  }
}

function makeDialogPart<P extends object>(
  pick: (kit: DialogParts) => React.FC<P>,
) {
  return function DialogPart(props: P & OptionalTenantProp) {
    const { tenant, ...rest } = props;
    const Kit = useDialogKit(tenant);
    const Comp = pick(Kit);
    return <Comp {...(rest as P)} />;
  };
}

export const Dialog = makeDialogPart<DialogPrimitive.Root.Props>(
  (k) => k.Root,
) as React.FC<DialogPrimitive.Root.Props> & {
  Trigger: React.FC<DialogPrimitive.Trigger.Props>;
  Portal: React.FC<DialogPrimitive.Portal.Props>;
  Close: React.FC<DialogPrimitive.Close.Props>;
  Backdrop: React.FC<DialogPrimitive.Backdrop.Props>;
  Popup: React.FC<DialogPrimitive.Popup.Props>;
  Header: React.FC<React.ComponentProps<"div">>;
  Title: React.FC<React.ComponentProps<"div">>;
  Description: React.FC<DialogDescriptionProps>;
};

Dialog.Trigger = makeDialogPart<DialogPrimitive.Trigger.Props>(
  (k) => k.Trigger,
);
Dialog.Portal = makeDialogPart<DialogPrimitive.Portal.Props>((k) => k.Portal);
Dialog.Close = makeDialogPart<DialogPrimitive.Close.Props>((k) => k.Close);
Dialog.Backdrop = makeDialogPart<DialogPrimitive.Backdrop.Props>(
  (k) => k.Backdrop,
);
Dialog.Header = makeDialogPart<React.ComponentProps<"div">>((k) => k.Header);
Dialog.Title = makeDialogPart<React.ComponentProps<"div">>((k) => k.Title);
Dialog.Description = makeDialogPart<DialogDescriptionProps>(
  (k) => k.Description,
);
Dialog.Popup = makeDialogPart<DialogPrimitive.Popup.Props>((k) => k.Popup);
